import PricingTab from "@/pages/_clients/PricingTab";
import { ReciboDetalleModal } from "@/components/ReciboPago";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ClientHistoryTab from "@/pages/_clients/ClientHistoryTab";
import CreditLogTab from "@/pages/_clients/CreditLogTab";
import CambiosTab, { UltimaActualizacion } from "@/pages/_clients/CambiosTab";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import TemplatePicker, { withTemplate } from "@/components/TemplatePicker";
import { fmtL, fmtNum, fmtDate, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import DocumentCenter from "@/components/DocumentCenter";
import StatementExportModal from "@/components/StatementExportModal";
import DocumentoPdfModal from "@/components/DocumentoPdfModal";
import { EmailHistory } from "@/components/DocEmail";
import {
  ArrowLeftIcon, UserIcon, BankIcon, MapPinIcon, FileTextIcon, ClockIcon,
  WarningOctagonIcon, EnvelopeIcon, PrinterIcon, PlusIcon, TrashIcon, BuildingsIcon, LockKeyIcon, TagIcon, XIcon, TruckIcon, EyeIcon,
  FilePdfIcon, FileXlsIcon, ReceiptIcon, ArrowsLeftRightIcon, TrayArrowDownIcon,
  CheckCircleIcon, LockOpenIcon
} from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { getToken } from "@/lib/token";
import { pedirPaseFactura, conPase } from "@/components/NotaDescarga";
import { CambioProyectoModal, MoverNcModal, useGestor } from "@/pages/_payments/CarteraCliente";

const TABS = [
  { id: "info", label: "Información", icon: UserIcon },
  { id: "balance", label: "Estado de cuenta", icon: BankIcon },
  { id: "periodo", label: "Historial por período", icon: ClockIcon },
  { id: "bitacora", label: "Bitácora de cobros", icon: FileTextIcon },
  { id: "projects", label: "Proyectos", icon: BuildingsIcon },
  { id: "shipping", label: "Destinos de envío", icon: MapPinIcon },
  { id: "pricing", label: "Precios por producto", icon: TagIcon },
  { id: "documents", label: "Documentos", icon: FileTextIcon },
  { id: "banks", label: "Cuentas bancarias", icon: BankIcon },
  { id: "history", label: "Historial", icon: ClockIcon },
  { id: "cambios", label: "Historial de cambios", icon: ClockIcon },
];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission, user } = useAuth();
  // Pestaña en la URL: al abrir un documento y regresar se conserva (con los filtros del historial)
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "info";
  const setTab = (t) => { const n = new URLSearchParams(sp); n.set("tab", t); setSp(n, { replace: true }); };
  const [pedirDesbloqueo, setPedirDesbloqueo] = useState(false);
  const [exportEC, setExportEC] = useState(null);   // exportar estado de cuenta: selección de cuentas
  const [client, setClient] = useState(null);
  const [balance, setBalance] = useState(null);
  const [statement, setStatement] = useState(null);
  const [projects, setProjects] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [docs, setDocs] = useState([]);
  const [banks, setBanks] = useState([]);
  const [reps, setReps] = useState([]);

  const load = async () => {
    try {
      const [{ data: c }, { data: b }, { data: s }, { data: pr }, { data: sh }, { data: d }, { data: bk }, { data: r }] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/balance`),
        api.get(`/clients/${id}/statement`),
        // Secciones complementarias: si el rol no tiene acceso a alguna (p. ej. el RTC a las cuentas bancarias
        // del cliente), la ficha igual se abre con esa sección vacía
        api.get(`/clients/${id}/projects`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/shipping-destinations`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/documents`).catch(() => ({ data: [] })),
        api.get(`/clients/${id}/bank-accounts`).catch(() => ({ data: [] })),
        api.get(`/representatives`).catch(() => ({ data: [] })),
      ]);
      setClient(c); setBalance(b); setStatement(s);
      setProjects(pr); setShipping(sh); setDocs(d); setBanks(bk); setReps(r);
    } catch (e) { toast.error(apiErr(e)); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (!client) return <Layout title="Cargando…">…</Layout>;

  return (
    <Layout
      title={
        <span className="flex items-center gap-2">
          <span className="font-mono text-agro-green" data-testid="client-header-codigo">{client.codigo_cliente || client.codigo}</span>
          <span className="text-text-secondary"> · </span>
          <span>{client.nombre}</span>
          {balance?.bloqueado && (
            <span title={balance.razon_bloqueo} className="ml-2 inline-flex items-center gap-1 text-red-400 text-xs bg-red-500/10 border border-red-500/40 rounded px-2 py-0.5">
              <LockKeyIcon size={12} weight="fill" /> Bloqueado
            </span>
          )}
        </span>
      }
      subtitle={`${client.region || ""} · ${client.rtc_asignado_nombre || "Sin RTC"} · Nivel ${client.nivel_precio?.toUpperCase()}`}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <TemplatePicker tipo="estado_cuenta" />
          <button
            onClick={() => setExportEC("pdf")}
            data-testid="statement-pdf-btn"
            className="border border-blue-500/50 hover:bg-blue-500/10 text-blue-300 rounded-md px-3 py-2 text-sm flex items-center gap-2 font-medium"
          >
            <FilePdfIcon size={16} aria-hidden="true" /> Estado de cuenta (PDF)
          </button>
          <button
            onClick={() => setExportEC("excel")}
            data-testid="statement-excel-btn"
            className="border border-agro-green/50 hover:bg-agro-green/10 text-agro-green rounded-md px-3 py-2 text-sm flex items-center gap-2 font-medium"
          >
            <FileXlsIcon size={16} aria-hidden="true" /> Estado de cuenta (Excel)
          </button>
          {balance?.bloqueado && (
            <button onClick={() => { setTab("bitacora"); setPedirDesbloqueo(true); }} data-testid="header-solicitar-desbloqueo"
              className="border border-agro-yellow/50 text-agro-yellow rounded-md px-3 py-2 text-sm">Solicitar desbloqueo</button>
          )}
          <button onClick={() => navigate("/clientes")}
            data-testid="back-clients-btn"
            className="border border-border hover:border-agro-green/40 rounded-md px-3 py-2 text-sm flex items-center gap-2 text-text-secondary hover:text-text-primary">
            <ArrowLeftIcon size={16} /> Volver
          </button>
        </div>
      }
    >
      {/* KPIs balance */}
      {balance && <BalanceKPIs balance={balance} />}

      <UltimaActualizacion u={client.ultima_actualizacion} onVer={() => setTab("cambios")} />

      {/* Tabs */}
      <div className="border-b border-border mb-4 flex gap-1 overflow-x-auto mt-6" data-testid="client-detail-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 whitespace-nowrap transition-all ${
              tab === t.id
                ? "border-agro-green text-agro-green font-medium"
                : "border-transparent text-text-secondary hover:text-text-primary"
            }`}
          >
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "info" && <InfoTab client={client} reps={reps} onUpdate={load} />}
      {tab === "balance" && <BalanceTab cid={id} balance={balance} statement={statement} client={client} onReload={load} hasPermission={hasPermission}
                                         onBitacora={() => setTab("bitacora")} onPeriodo={() => setTab("periodo")} />}
      {tab === "projects" && <ProjectsTab cid={id} items={projects} onChange={load} />}
      {tab === "shipping" && <ShippingTab cid={id} items={shipping} onChange={load} />}
      {tab === "pricing" && <PricingTab cid={id} client={client} />}
      {tab === "documents" && <DocumentsTab cid={id} items={docs} onChange={load} />}
      {tab === "banks" && <BanksTab cid={id} items={banks} onChange={load} />}
      {tab === "history" && <HistoryTab statement={statement} />}
      {tab === "cambios" && <CambiosTab cid={id} />}
      {tab === "periodo" && <ClientHistoryTab cid={id} hoy={user?.hoy_empresa || new Date().toISOString().slice(0, 10)} />}
      {tab === "bitacora" && <CreditLogTab cid={id} client={client} balance={balance} onReload={load}
                                           abrirSolicitud={pedirDesbloqueo} onSolicitudCerrada={() => setPedirDesbloqueo(false)} />}
      {exportEC && (
        <StatementExportModal clienteId={id} clienteCodigo={client.codigo} clienteNombre={client.nombre}
          formato={exportEC} onClose={() => setExportEC(null)} />
      )}
    </Layout>
  );
}

function BalanceKPIs({ balance }) {
  const Kpi = ({ label, value, color }) => (
    <div className="bg-card border border-border/60 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">{label}</div>
      <div className={`text-xl font-heading font-semibold ${color}`}>{value}</div>
    </div>
  );
  const pct = Math.min(balance.porcentaje_uso_credito, 100);
  const barColor = balance.bloqueado ? "bg-red-400" : pct >= 80 ? "bg-agro-yellow" : "bg-agro-green";
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Saldo total" value={fmtL(balance.saldo_total)} color="text-text-primary" />
        <Kpi label="Vencido" value={fmtL(balance.saldo_vencido)} color="text-red-400" />
        <Kpi label="Por vencer" value={fmtL(balance.saldo_por_vencer)} color="text-agro-yellow" />
        <Kpi label="NC disponibles" value={fmtL(balance.notas_credito_disponibles)} color="text-agro-green" />
      </div>
      {balance.limite_credito > 0 && (
        <div className="bg-card border border-border/60 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs text-text-secondary">
              Uso de crédito · <strong>{fmtL(balance.saldo_total)}</strong> de <strong>{fmtL(balance.limite_credito)}</strong>
            </div>
            <div className={`text-sm font-semibold ${balance.bloqueado ? "text-red-400" : pct >= 80 ? "text-agro-yellow" : "text-agro-green"}`}>
              {balance.porcentaje_uso_credito}%
            </div>
          </div>
          <div className="h-2 bg-app rounded-full overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }}></div>
          </div>
          {balance.bloqueado && (
            <div className="mt-3 p-3 rounded border border-red-500/30 bg-red-500/5 text-red-400 text-xs flex items-start gap-2">
              <WarningOctagonIcon size={14} weight="fill" className="mt-0.5" />
              <div>
                <strong>Bloqueado para crédito:</strong> {balance.razon_bloqueo}.<br />
                Las ventas a crédito requieren la autorización del Administrador: envía una solicitud de desbloqueo desde la
                pestaña “Bitácora de cobros”. Los pagos y las gestiones de cobro se pueden registrar normalmente.
              </div>
            </div>
          )}
          {balance.autorizado_vigente && (
            <div className="mt-3 p-3 rounded border border-agro-green/30 bg-agro-green/5 text-agro-green text-xs">
              <CheckCircleIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Autorización temporal vigente hasta {fmtDate(balance.autorizado_hasta)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoTab({ client, reps, onUpdate }) {
  const { user: usuario } = useAuth();
  const esAdmin = usuario?.role === "admin" || Boolean(usuario?.effective?.is_admin);   // tarifa: solo el Administrador General
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState(client);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setF(client), [client]);
  const save = async () => {
    try { await api.put(`/clients/${client.id}`, f); toast.success("Cliente actualizado"); setEdit(false); onUpdate(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-lg font-semibold">Ficha del cliente</h3>
        {!edit ? (
          <button onClick={() => setEdit(true)} data-testid="edit-client-btn"
            className="px-3 py-1.5 text-xs border border-border rounded-md hover:border-agro-green/40">Editar</button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { setEdit(false); setF(client); }} className="px-3 py-1.5 text-xs border border-border rounded-md">Cancelar</button>
            <button onClick={save} data-testid="save-client-btn" className="px-3 py-1.5 text-xs bg-agro-green text-black rounded-md font-medium">Guardar</button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Fld label="Código de cliente" v={f.codigo_cliente || "—"} ro />
        <Fld label="Referencia anterior" v={f.codigo} ro />
        <Fld label="Nombre / Razón social" v={f.nombre} on={(v) => setF({ ...f, nombre: v })} ro={!edit} />
        <Fld label="RTN" v={f.rtn} on={(v) => setF({ ...f, rtn: v })} ro={!edit} />
        <Fld label="Identidad" v={f.identidad} on={(v) => setF({ ...f, identidad: v })} ro={!edit} />
        <Fld label="Región" v={f.region} on={(v) => setF({ ...f, region: v })} ro={!edit} />
        <div>
          <L label="RTC asignado" />
          {edit ? (
            <select value={f.rtc_asignado_id || ""}
              onChange={(e) => {
                const r = reps.find((x) => x.id === e.target.value);
                setF({ ...f, rtc_asignado_id: e.target.value, rtc_asignado_nombre: r ? r.nombre : "" });
              }}
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
              <option value="">{reps.length ? "—" : "— No hay RTC registrados en RRHH —"}</option>
              {f.rtc_asignado_id && !reps.some((r) => r.id === f.rtc_asignado_id) && (
                <option value={f.rtc_asignado_id}>{f.rtc_asignado_nombre || "RTC"} (no disponible)</option>)}
              {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo} — {r.nombre}{r.region ? ` (${r.region})` : ""}</option>)}
            </select>
          ) : (
            <div className="text-sm text-text-primary py-2">{f.rtc_asignado_nombre || "—"}</div>
          )}
        </div>
        <Fld label="Segmento" v={f.segmento} on={(v) => setF({ ...f, segmento: v })} ro={!edit} />
        <div>
          <L label="Nivel de precio" />
          {edit && esAdmin ? (
            <select value={f.nivel_precio} onChange={(e) => setF({ ...f, nivel_precio: e.target.value })}
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
              <option value="p0">P0 — Especial (negocios especiales)</option>
              <option value="p1">P1 — Estándar</option>
              <option value="p2">P2 — Mayorista</option>
              <option value="p3">P3 — Distribuidor</option>
              <option value="p4">P4 — Corporativo</option>
            </select>
          ) : (
            <div className="text-sm text-text-primary py-2 uppercase font-medium text-agro-green">{f.nivel_precio}</div>
          )}
        </div>
        <Fld label="Teléfono" v={f.telefono} on={(v) => setF({ ...f, telefono: v })} ro={!edit} />
        <Fld label="Correo" v={f.email} on={(v) => setF({ ...f, email: v })} ro={!edit} />
        <Fld label="Dirección" v={f.direccion} on={(v) => setF({ ...f, direccion: v })} ro={!edit} />
        <Fld label="Días de crédito" type="number" v={f.dias_credito} on={(v) => setF({ ...f, dias_credito: Number(v) })} ro={!edit} />
        <Fld label="Límite de crédito (L.)" type="number" v={f.limite_credito} on={(v) => setF({ ...f, limite_credito: Number(v) })} ro={!edit} />
        <Fld label="Días para bloqueo" type="number" v={f.dias_bloqueo} on={(v) => setF({ ...f, dias_bloqueo: Number(v) })} ro={!edit} />
        <div className="md:col-span-3 flex items-center gap-3 pt-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!f.notificar_correo} disabled={!edit}
              onChange={(e) => setF({ ...f, notificar_correo: e.target.checked })}
              className="accent-agro-green w-4 h-4" />
            <span className="text-sm">Recibir notificaciones por correo</span>
          </label>
        </div>
      </div>

      <fieldset className="border border-border/60 rounded-lg p-4 mt-5">
        <legend className="px-2 text-[11px] uppercase tracking-wider text-agro-yellow font-semibold">Referencias (2)</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2].map((n) => (
            <div key={n} className="space-y-3">
              <Fld label={`Referencia ${n} — Nombre`} v={f[`referencia${n}_nombre`]}
                on={(v) => setF({ ...f, [`referencia${n}_nombre`]: v })} ro={!edit} />
              <Fld label="Parentesco" v={f[`referencia${n}_parentesco`]}
                on={(v) => setF({ ...f, [`referencia${n}_parentesco`]: v })} ro={!edit} />
              <Fld label="Teléfono" v={f[`referencia${n}_telefono`]}
                on={(v) => setF({ ...f, [`referencia${n}_telefono`]: v })} ro={!edit} />
            </div>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function L({ label }) {
  return <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>;
}

function Fld({ label, v, on, ro, type = "text" }) {
  return (
    <div>
      <L label={label} />
      {ro ? (
        <div className="text-sm text-text-primary py-2 min-h-[2.25rem]">{v || "—"}</div>
      ) : (
        <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)}
          className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
      )}
    </div>
  );
}

function BalanceTab({ cid, balance, statement, client, onReload, hasPermission, onBitacora, onPeriodo }) {
  // Acciones de cobranza por factura (las mismas de Pagos y Cobranza): Administrador y Delegado
  const { gestor } = useGestor();
  const navigate = useNavigate();
  const [accionFac, setAccionFac] = useState(null);     // {tipo: "proyecto"|"nc", f}
  const [authOpen, setAuthOpen] = useState(false);
  const [authData, setAuthData] = useState({ valido_hasta: "", motivo: "" });
  const canAuth = hasPermission("clients.override_credit_block");
  const [emailHist, setEmailHist] = useState([]);
  const loadEmailHist = () => api.get(`/clients/${cid}/statement/email-history`).then(({ data }) => setEmailHist(data || [])).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadEmailHist(); }, [cid]);
  const doAuth = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${cid}/credit-authorize`, authData); toast.success("Autorización registrada"); setAuthOpen(false); onReload(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const emailStatement = async (proyectoCodigo = null) => {
    // Envía el estado de cuenta por correo al cliente.
    // - Sin `proyectoCodigo` → estado completo (todos los proyectos).
    // - Con `proyectoCodigo` → solo ese proyecto adjuntado.
    const label = proyectoCodigo ? `el estado del proyecto "${proyectoCodigo}"` : "el estado de cuenta completo";
    if (!window.confirm(`¿Enviar por correo a ${client?.email || "el cliente"} ${label}?`)) return;
    try {
      const { data } = await api.post(`/clients/${cid}/statement/email`, {
        proyecto: proyectoCodigo || null,
      });
      toast.success(`Correo enviado a ${(data.to || []).join(", ") || client?.email}`);
      loadEmailHist();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  // Período (fecha de emisión) y criterio del estado de cuenta: únicamente facturas del cliente
  const [periodo, setPeriodo] = useState({ desde: "", hasta: "" });
  const [incluirPagadas, setIncluirPagadas] = useState(false);
  const [est, setEst] = useState(null);
  const [versionEst, setVersionEst] = useState(0);       // se recarga después de una acción sobre una factura
  useEffect(() => {
    if (!cid) return;
    const params = { incluir_pagadas: incluirPagadas };
    if (periodo.desde) params.desde = periodo.desde;
    if (periodo.hasta) params.hasta = periodo.hasta;
    api.get(`/reports/statements/${cid}/facturas`, { params }).then(({ data }) => setEst(data))
      .catch((e) => toast.error(apiErr(e)));
  }, [cid, periodo, incluirPagadas, versionEst]);
  const doPrint = (proyectoCodigo = null) => {
    // Solución iPad/Safari: construimos la URL con el token como query param
    // y abrimos como navegación directa. Safari maneja PDFs perfectamente cuando
    // vienen de un navigate directo (a diferencia de fetch → blob que rompe).
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const token = getToken();
    const companyId = localStorage.getItem("agroport_company_id");
    const branchId = localStorage.getItem("agroport_branch_id");
    const params = new URLSearchParams();
    params.set("token", token || "");
    if (companyId) params.set("company_id", companyId);
    if (branchId) params.set("branch_id", branchId);
    if (proyectoCodigo) params.set("proyecto", proyectoCodigo);
    if (periodo.desde) params.set("desde", periodo.desde);
    if (periodo.hasta) params.set("hasta", periodo.hasta);
    if (incluirPagadas) params.set("incluir_pagadas", "true");
    const url = withTemplate(`${apiBase}/reports/statements/${cid}/pdf?${params.toString()}`, "estado_cuenta");
    // Abrir en pestaña nueva. Si Safari bloquea el pop-up, cae a mismo tab.
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
  };
  const grupos = est?.grupos || [];
  const agrupado = Boolean(est?.agrupado);
  const [exportar, setExportar] = useState(null);   // "pdf" | "excel": selección de cuentas
  const [verDoc, setVerDoc] = useState(null);        // documento del estado de cuenta abierto en el visor
  const am = useModalForm({ value: authData, abierto: authOpen, onClose: () => setAuthOpen(false), onSave: doAuth });
  return (
    <div className="space-y-4">
      <div className="print:hidden">
        <EmailHistory items={emailHist} title="Historial de envíos del estado de cuenta" testid="statement-email-history" />
      </div>
      <div className="flex flex-wrap gap-2 print:hidden" data-testid="statement-links">
        <button onClick={onPeriodo} className="text-xs px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green">Historial por período</button>
        <button onClick={onBitacora} data-testid="statement-bitacora" className="text-xs px-3 py-1.5 border border-border rounded-md hover:border-agro-green hover:text-agro-green">
          Bitácora de cobros y compromisos de pago</button>
      </div>
      <div className="flex items-center justify-end gap-2 print:hidden flex-wrap" data-testid="statement-period">
        <span className="text-[10px] uppercase tracking-wider text-text-muted">Facturas emitidas</span>
        <input type="date" value={periodo.desde} onChange={(e) => setPeriodo((p) => ({ ...p, desde: e.target.value }))}
          data-testid="statement-desde" title="Desde (vacío = inicio de la cuenta)"
          className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
        <input type="date" value={periodo.hasta} onChange={(e) => setPeriodo((p) => ({ ...p, hasta: e.target.value }))}
          data-testid="statement-hasta" title="Hasta / fecha de corte (vacío = hoy)"
          className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
        <label className="flex items-center gap-1.5 text-xs text-text-secondary">
          <input type="checkbox" checked={incluirPagadas} onChange={(e) => setIncluirPagadas(e.target.checked)}
            data-testid="statement-incluir-pagadas" className="accent-agro-green" /> Incluir canceladas
        </label>
        <button onClick={() => setExportar("pdf")} data-testid="statement-pdf-periodo"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-agro-green/50 text-agro-green rounded-md hover:bg-agro-green/10">
          <PrinterIcon size={13} weight="bold" /> PDF del período
        </button>
        <button onClick={() => setExportar("excel")} data-testid="statement-excel-periodo"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-agro-green/50 text-agro-green rounded-md hover:bg-agro-green/10">
          Exportar estado de cuenta a Excel
        </button>
        {balance?.bloqueado && canAuth && (
          <button onClick={() => setAuthOpen(true)} data-testid="authorize-credit-btn"
            className="px-3 py-2 text-sm border border-agro-yellow/40 text-agro-yellow rounded-md hover:bg-agro-yellow/10">
            <LockOpenIcon size={14} className="inline align-[-2px]" aria-hidden="true" /> Autorizar crédito
          </button>
        )}
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden print:bg-white print:text-black print:rounded-none print:border-0" id="statement-print">
        <div className="px-6 py-4 border-b border-border print:border-gray-300 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg text-text-primary print:text-black">{client?.nombre}</div>
            <div className="text-xs text-text-muted print:text-gray-600">{client?.rtn ? `RTN ${client.rtn}` : ""} · {client?.email}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-500">Estado de cuenta al</div>
            <div className="text-sm font-medium text-text-primary print:text-black">{new Date().toLocaleDateString("es-HN", { dateStyle: "long" })}</div>
          </div>
        </div>

        {grupos.length === 0 ? (
          <div className="p-8 text-center text-text-muted print:text-gray-500" data-testid="statement-empty">
            {incluirPagadas ? "Sin documentos en el período" : "Sin facturas ni notas de débito con saldo pendiente"}
          </div>
        ) : grupos.map((g, gi) => (
          <div key={g.codigo} className={gi > 0 ? "border-t-4 border-agro-green/30 print:border-gray-400" : ""}
               data-testid={`statement-group-${g.codigo}`}>
            {agrupado && (
              <div className="px-6 py-3 bg-sidebar/60 border-b border-border/60 print:bg-gray-50 print:border-gray-300 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-text-muted print:text-gray-500">Cuenta:</span>
                  <span className="font-heading text-sm font-semibold text-agro-green print:text-black">
                    {g.codigo_subcuenta && <span className="font-mono mr-2" data-testid={`statement-subcuenta-${g.codigo}`}>{g.codigo_subcuenta}</span>}
                    {g.etiqueta || (g.codigo === "SIN SUBPROYECTO" ? "CUENTA PRINCIPAL" : `SUBCUENTA ${g.codigo}${g.nombre ? ` · ${g.nombre}` : ""}`)}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-[10px] text-text-muted print:text-gray-500">{g.facturas.length} documento{g.facturas.length !== 1 ? "s" : ""}</div>
                  <button onClick={() => doPrint(g.codigo)} data-testid={`statement-pdf-${g.codigo}`}
                    className="print:hidden inline-flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wider bg-agro-green/10 text-agro-green border border-agro-green/40 rounded hover:bg-agro-green/20"
                    title="Descargar solo esta cuenta en PDF">
                    <PrinterIcon size={12} weight="bold" /> PDF
                  </button>
                  <button onClick={() => emailStatement(g.codigo)} data-testid={`statement-email-${g.codigo}`}
                    className="print:hidden inline-flex items-center gap-1.5 px-3 py-1 text-[11px] uppercase tracking-wider bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/40 rounded hover:bg-agro-yellow/20"
                    title="Enviar por correo solo esta cuenta">
                    <EnvelopeIcon size={12} weight="bold" /> Enviar
                  </button>
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted print:bg-gray-100 print:text-gray-700">
                <tr className="text-left">
                  <th className="p-2 pl-5 w-10">#</th>
                  <th className="p-2">Tipo</th>
                  <th className="p-2">Número</th>
                  <th className="p-2">Fecha</th>
                  <th className="p-2">Vencimiento</th>
                  <th className="p-2 text-right">Importe</th>
                  <th className="p-2 text-right">Saldo pendiente</th>
                  <th className="p-2 pr-5 text-right print:hidden">{gestor ? "Acciones" : "Documento"}</th>
                </tr>
              </thead>
              <tbody>
                {g.facturas.map((f, i) => (
                  <tr key={f.id} className="border-t border-border/40 print:border-gray-100" data-testid={`statement-inv-${f.numero}`}>
                    <td className="p-2 pl-5 text-xs text-text-muted">{i + 1}</td>
                    <td className="p-2 text-xs">
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded border ${f.tipo === "nota_debito" ? "border-agro-yellow/40 text-agro-yellow" : "border-agro-green/40 text-agro-green"}`}>
                        {f.tipo_label || "Factura"}</span>
                    </td>
                    <td className="p-2 text-xs font-mono">
                      {f.numero}
                      {f.vencida && <span className="ml-2 text-[10px] text-red-300">Vencida · {f.dias_vencido} d</span>}
                      {f.factura_referencia && <div className="text-[10px] text-text-muted font-sans">Ref. factura {f.factura_referencia}</div>}
                    </td>
                    <td className="p-2 text-xs">{fmtDate(f.fecha)}</td>
                    <td className="p-2 text-xs">{fmtDate(f.fecha_vencimiento)}</td>
                    <td className="p-2 text-right text-sm">{fmtL(f.importe)}</td>
                    <td className="p-2 pr-5 text-right text-sm font-semibold">
                      {f.incluida_en ? <span className="text-[11px] font-normal text-text-muted" title="Nota de débito anterior: su saldo está dentro de la factura">Incluido en factura {f.incluida_en}</span> : fmtL(f.saldo)}
                    </td>
                    <td className="p-2 pr-5 text-right print:hidden">
                      <button type="button" onClick={async () => {
                        if (f.tipo === "nota_debito") { setVerDoc(f); return; }
                        const pase = await pedirPaseFactura(f);          // RTC: nota de descarga ("" para los demás)
                        if (pase !== null) setVerDoc({ ...f, pase });
                      }} data-testid={`statement-ver-${f.numero}`}
                        title={f.tipo === "nota_debito" ? "Ver la nota de débito (PDF oficial)" : "Ver la factura (PDF oficial)"}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border border-agro-green/40 text-agro-green rounded hover:bg-agro-green/10 whitespace-nowrap">
                        <EyeIcon size={12} /> {f.tipo === "nota_debito" ? "Ver nota de débito" : "Ver factura"}
                      </button>
                      {gestor && f.tipo === "factura" && !f.en_nc && (
                        <span className="inline-flex align-middle ml-1.5 gap-0.5" data-testid={`statement-acciones-${f.numero}`}>
                          {f.saldo > 0.004 && (
                            <button type="button" onClick={() => navigate(`/cxc?cobrar=${encodeURIComponent(f.id)}&cliente=${encodeURIComponent(cid)}`)}
                              title="Recibir pago" aria-label="Recibir pago" data-testid={`statement-pago-${f.numero}`}
                              className="p-1.5 rounded text-text-muted hover:text-agro-green hover:bg-white/[0.05]"><ReceiptIcon size={16} /></button>
                          )}
                          <button type="button" onClick={() => setAccionFac({ tipo: "proyecto", f })} title="Cambiar proyecto o subcuenta" aria-label="Cambiar proyecto o subcuenta"
                            data-testid={`statement-proyecto-${f.numero}`} className="p-1.5 rounded text-text-muted hover:text-agro-green hover:bg-white/[0.05]"><ArrowsLeftRightIcon size={16} /></button>
                          {f.saldo > 0.004 && (
                            <button type="button" onClick={() => setAccionFac({ tipo: "nc", f })} title="Mover a subcuenta NC" aria-label="Mover a subcuenta NC"
                              data-testid={`statement-nc-${f.numero}`} className="p-1.5 rounded text-text-muted hover:text-agro-green hover:bg-white/[0.05]"><TrayArrowDownIcon size={16} /></button>
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              {/* Total de la cuenta: siempre al cierre de cada cuenta */}
              {(
                <tfoot className="bg-sidebar/60 print:bg-gray-50 font-semibold">
                  <tr>
                    <td colSpan="5" className="p-2 pl-5 text-right text-xs text-text-muted print:text-gray-600">
                      TOTAL DE LA CUENTA: {g.etiqueta || (g.codigo === "SIN SUBPROYECTO" ? "CUENTA PRINCIPAL" : `SUBCUENTA ${g.codigo}`)}</td>
                    <td className="p-2" />
                    <td className="p-2 text-right text-sm text-agro-green print:text-black">{fmtL(g.saldo)}</td>
                    <td className="p-2 pr-5 print:hidden" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ))}

        <div className="bg-sidebar print:bg-gray-100 font-semibold px-6 py-4 flex items-center justify-between border-t border-border/60 print:border-gray-300">
          <div className="text-sm">
            TOTAL GENERAL
            {est && <span className="ml-3 text-[11px] font-normal text-text-muted">
              Vencido {fmtL(est.saldo_vencido)} · Por vencer {fmtL(est.saldo_por_vencer)}
            </span>}
          </div>
          <div className="text-lg font-heading text-agro-green print:text-black" data-testid="statement-total">{fmtL(est?.total_saldo || 0)}</div>
        </div>
        {Number(est?.saldo_en_nc || 0) > 0.004 && (
          <div className="px-6 py-2 text-[11px] text-text-muted border-t border-border/40 print:text-gray-600" data-testid="statement-en-nc">
            En proceso de nota de crédito (no incluido en este estado): {fmtL(est.saldo_en_nc)}
            {Number(est.saldo_en_nc_pendiente || 0) > 0.004 && <> · pendiente de autorización {fmtL(est.saldo_en_nc_pendiente)}</>}
            {Number(est.saldo_en_nc_autorizado || 0) > 0.004 && <> · autorizado {fmtL(est.saldo_en_nc_autorizado)}</>}
          </div>
        )}
      </div>

      {accionFac?.tipo === "proyecto" && <CambioProyectoModal factura={accionFac.f} onClose={() => setAccionFac(null)} onDone={() => { setAccionFac(null); setVersionEst((v) => v + 1); onReload && onReload(); }} />}
      {accionFac?.tipo === "nc" && <MoverNcModal factura={accionFac.f} onClose={() => setAccionFac(null)} onDone={() => { setAccionFac(null); setVersionEst((v) => v + 1); onReload && onReload(); }} />}
      {verDoc && (
        <DocumentoPdfModal
          url={verDoc.tipo === "nota_debito" ? `/debit-notes/${verDoc.id}/pdf` : conPase(withTemplate(`/invoices/${verDoc.id}/pdf`, "factura"), verDoc.pase)}
          titulo={verDoc.tipo === "nota_debito" ? "Nota de débito" : "Factura"} numero={verDoc.numero}
          archivo={`${verDoc.tipo === "nota_debito" ? "nota-debito" : "factura"}-${verDoc.numero}.pdf`}
          onClose={() => setVerDoc(null)} testid="statement-documento" />
      )}
      {exportar && (
        <StatementExportModal clienteId={cid} clienteCodigo={client?.codigo} clienteNombre={client?.nombre}
          formato={exportar} periodo={periodo} incluirPagadas={incluirPagadas} onClose={() => setExportar(null)} />
      )}
      {authOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...am.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border"><h3 className="font-heading text-lg font-semibold">Autorizar crédito</h3></div>
            <form onSubmit={am.guardar} className="p-5 space-y-4">
              <div>
                <L label="Válido hasta *" />
                <input type="date" required value={authData.valido_hasta}
                  data-testid="auth-valido-hasta"
                  onChange={(e) => setAuthData({ ...authData, valido_hasta: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div>
                <L label="Motivo" />
                <textarea rows="2" value={authData.motivo}
                  data-testid="auth-motivo"
                  onChange={(e) => setAuthData({ ...authData, motivo: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
              </div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={am.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button disabled={am.saving} type="submit" data-testid="auth-submit" className="px-5 py-2 text-sm bg-agro-yellow text-black font-medium rounded-md">Autorizar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @media print {
          @page { size: A4; margin: 1.2cm; }
          html, body { background: white !important; color: black !important; }
          [data-testid="app-sidebar"], [data-testid="app-header"], [data-testid="client-detail-tabs"], .print\\:hidden { display: none !important; }
          [data-testid="page-main"] { padding: 0 !important; margin: 0 !important; }
          #statement-print, #statement-print * { background: white !important; color: black !important; border-color: #ddd !important; }
          .print-page-break { break-before: page; page-break-before: always; }
        }
      `}</style>
    </div>
  );
}

function ProjectsTab({ cid, items, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ codigo: "", nombre: "", descripcion: "", estado: "activo" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${cid}/projects`, f); toast.success("Proyecto creado"); setOpen(false); setF({ codigo: "", nombre: "", descripcion: "", estado: "activo" }); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Eliminar ${p.nombre}?`)) return;
    try { await api.delete(`/clients/${cid}/projects/${p.id}`); toast.success("Eliminado"); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const fm = useModalForm({ value: f, abierto: open, onClose: () => setOpen(false), onSave: save });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="new-project-btn"
          className="bg-agro-green text-black px-3 py-2 text-sm rounded-md font-medium flex items-center gap-2">
          <PlusIcon size={14} weight="bold" /> Nuevo proyecto
        </button>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Código</th>
              <th className="p-3">Proyecto</th>
              <th className="p-3 text-center">Estado</th>
              <th className="p-3 text-right">Facturas</th>
              <th className="p-3 text-right">Vencido</th>
              <th className="p-3 text-right">Saldo pendiente</th>
              <th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-text-muted">Sin proyectos</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-t border-border/40" data-testid={`project-row-${p.codigo}`}>
                <td className="p-3 pl-5 font-mono text-xs text-agro-green" data-testid={`project-codigo-${p.codigo}`}>
                  {p.codigo_subcuenta || (p.virtual ? "—" : "")}
                  {p.codigo && p.codigo !== p.codigo_subcuenta && <div className="text-[10px] text-text-muted">{p.codigo}</div>}
                </td>
                <td className="p-3">
                  <div className="text-sm flex items-center gap-2">
                    {p.nombre}
                    {p.virtual && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30" title="Proyecto detectado en facturas pero sin registro. Créalo formalmente para gestionarlo.">
                        virtual
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-text-muted">{p.descripcion}</div>
                </td>
                <td className="p-3 text-center"><span className="px-2 py-0.5 rounded text-[10px] bg-app border border-border">{p.estado}</span></td>
                <td className="p-3 text-right text-xs">
                  <div>{p.facturas_pendientes || 0} <span className="text-text-muted">pend.</span></div>
                  <div className="text-[10px] text-text-muted">{p.facturas_total || 0} total</div>
                </td>
                <td className="p-3 text-right text-xs text-red-400 font-medium">{fmtL(p.vencido || 0)}</td>
                <td className="p-3 text-right font-medium text-agro-yellow">{fmtL(p.saldo_pendiente ?? p.saldo_actual ?? 0)}</td>
                <td className="p-3 pr-5 text-right">
                  {!p.virtual && (
                    <button aria-label="Eliminar" onClick={() => del(p)} data-testid={`project-del-${p.codigo}`} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...fm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border"><h3 className="font-heading text-lg font-semibold">Nuevo proyecto</h3></div>
            <form onSubmit={fm.guardar} className="p-5 space-y-3">
              <div className="text-xs text-text-secondary bg-app border border-border rounded-md px-3 py-2">
                Código de subcuenta: <b className="font-mono text-agro-green">se asigna automáticamente</b> (código del cliente + secuencia, p. ej. CLI-00441-001).
              </div>
              <div><L label="Código corto (opcional)" /><input data-testid="proj-codigo" value={f.codigo} onChange={(e) => setF({ ...f, codigo: e.target.value.toUpperCase() })} placeholder="P. ej. FINCA-NORTE" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green font-mono uppercase" /></div>
              <div><L label="Nombre *" /><input required data-testid="proj-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div><L label="Descripción" /><textarea rows="2" value={f.descripcion} onChange={(e) => setF({ ...f, descripcion: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={fm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button disabled={fm.saving} type="submit" data-testid="proj-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingTab({ cid, items, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ alias: "", direccion: "", encargado: "", telefono: "", referencia: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${cid}/shipping-destinations`, f); toast.success("Destino creado"); setOpen(false); setF({ alias: "", direccion: "", encargado: "", telefono: "", referencia: "" }); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Eliminar ${s.alias}?`)) return;
    try { await api.delete(`/clients/${cid}/shipping-destinations/${s.id}`); toast.success("Eliminado"); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const fm = useModalForm({ value: f, abierto: open, onClose: () => setOpen(false), onSave: save });
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-xs text-text-muted">{items.length} / 5 destinos</div>
        <button disabled={items.length >= 5} onClick={() => setOpen(true)} data-testid="new-shipping-btn"
          className="bg-agro-green text-black px-3 py-2 text-sm rounded-md font-medium flex items-center gap-2 disabled:opacity-40">
          <PlusIcon size={14} weight="bold" /> Nuevo destino
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 ? (
          <div className="col-span-2 p-8 text-center text-text-muted bg-card border border-border/60 rounded-lg">Sin destinos registrados</div>
        ) : items.map((s) => (
          <ShippingCard key={s.id} s={s} cid={cid} onDelete={() => del(s)} />
        ))}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...fm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border"><h3 className="font-heading text-lg font-semibold">Nuevo destino de envío</h3></div>
            <form onSubmit={fm.guardar} className="p-5 space-y-3">
              <div><L label="Alias *" /><input required data-testid="ship-alias" value={f.alias} onChange={(e) => setF({ ...f, alias: e.target.value })} placeholder="ej. Finca El Naranjo" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div><L label="Dirección *" /><input required data-testid="ship-direccion" value={f.direccion} onChange={(e) => setF({ ...f, direccion: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><L label="Encargado" /><input data-testid="ship-encargado" value={f.encargado} onChange={(e) => setF({ ...f, encargado: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
                <div><L label="Teléfono" /><input data-testid="ship-telefono" value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              </div>
              <div><L label="Referencia / punto de referencia" /><input value={f.referencia} onChange={(e) => setF({ ...f, referencia: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={fm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button disabled={fm.saving} type="submit" data-testid="ship-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Crear destino</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ShippingCard({ s, cid, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (expanded) { setExpanded(false); return; }
    if (!history) {
      setLoading(true);
      try {
        const { data } = await api.get(`/clients/${cid}/shipping-destinations/${s.id}/invoices`);
        setHistory(data);
      } catch (e) { toast.error(apiErr(e) || "Error"); }
      finally { setLoading(false); }
    }
    setExpanded(true);
  };

  return (
    <div className="bg-card border border-border/60 rounded-lg p-4" data-testid={`shipping-card-${s.alias}`}>
      <div className="flex justify-between items-start mb-2">
        <div>
          <div className="font-heading font-semibold text-text-primary flex items-center gap-2"><MapPinIcon size={14} className="text-agro-green" /> {s.alias}</div>
          <div className="text-xs text-text-secondary mt-1">{s.direccion}</div>
        </div>
        <button aria-label="Eliminar" onClick={onDelete} className="text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
      </div>
      <div className="text-[11px] text-text-muted space-y-0.5 pt-2 border-t border-border/40">
        {s.encargado && <div>Encargado: <span className="text-text-secondary">{s.encargado}</span></div>}
        {s.telefono && <div>Teléfono: <span className="text-text-secondary">{s.telefono}</span></div>}
        {s.referencia && <div>Referencia: <span className="text-text-secondary">{s.referencia}</span></div>}
      </div>
      <button
        onClick={toggle}
        data-testid={`ship-history-toggle-${s.alias}`}
        className="mt-3 w-full text-xs bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-md py-1.5 flex items-center justify-center gap-2 transition"
      >
        <TruckIcon size={12} weight={expanded ? "fill" : "regular"} />
        {expanded ? "Ocultar" : "Ver historial de envíos"}
      </button>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-blue-500/20" data-testid={`ship-history-${s.alias}`}>
          {loading ? (
            <div className="text-center text-xs text-text-muted py-3">Cargando…</div>
          ) : !history || history.count === 0 ? (
            <div className="text-center text-xs text-text-muted py-3 italic">Sin facturas enviadas a este destino</div>
          ) : (
            <>
              <div className="flex justify-between items-center mb-2 text-[11px]">
                <span className="text-text-muted">{history.count} envío(s)</span>
                <div className="flex gap-3">
                  <span>Total: <span className="font-mono text-text-primary">L {history.total_facturado.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</span></span>
                  {history.total_pendiente > 0 && (
                    <span>Pdte: <span className="font-mono text-red-300">L {history.total_pendiente.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</span></span>
                  )}
                </div>
              </div>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {history.facturas.map((f) => (
                  <div key={f.id} className="flex justify-between items-center text-[11px] p-1.5 hover:bg-white/[0.03] rounded" data-testid={`ship-inv-${f.numero}`}>
                    <div>
                      <span className="font-mono text-agro-green">{f.numero}</span>
                      <span className="text-text-muted ml-2">{f.fecha?.slice(0, 10)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] ${f.estado === "pagada" ? "bg-agro-green/15 text-agro-green" : f.estado === "parcial" ? "bg-agro-yellow/15 text-agro-yellow" : "bg-blue-500/15 text-blue-300"}`}>
                        {f.estado}
                      </span>
                      <span className="font-mono text-text-primary">L {f.total.toLocaleString("es-HN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
function DocumentsTab({ cid, items, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ tipo: "id", nombre: "", url: "", fecha_emision: "", fecha_vencimiento: "", notas: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${cid}/documents`, f); toast.success("Documento agregado"); setOpen(false); setF({ tipo: "id", nombre: "", url: "", fecha_emision: "", fecha_vencimiento: "", notas: "" }); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (d) => {
    if (!window.confirm(`Eliminar ${d.nombre}?`)) return;
    try { await api.delete(`/clients/${cid}/documents/${d.id}`); toast.success("Eliminado"); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const TIPO_LABEL = { id: "Identidad", licencia: "Licencia", rtn: "RTN", escritura: "Escritura", letra_cambio: "Letra de cambio", otro: "Otro" };
  const fm = useModalForm({ value: f, abierto: open, onClose: () => setOpen(false), onSave: save });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="new-doc-btn"
          className="bg-agro-green text-black px-3 py-2 text-sm rounded-md font-medium flex items-center gap-2">
          <PlusIcon size={14} weight="bold" /> Agregar documento
        </button>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Tipo</th>
              <th className="p-3">Nombre</th>
              <th className="p-3">Emisión</th>
              <th className="p-3">Vencimiento</th>
              <th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="5" className="p-8 text-center text-text-muted">Sin documentos</td></tr>
            ) : items.map((d) => (
              <tr key={d.id} className="border-t border-border/40" data-testid={`doc-row-${d.id}`}>
                <td className="p-3 pl-5 text-xs"><span className="px-2 py-0.5 rounded bg-app border border-border">{TIPO_LABEL[d.tipo] || d.tipo}</span></td>
                <td className="p-3">
                  <div className="text-sm">{d.nombre}</div>
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="text-[11px] text-agro-green hover:underline">Ver archivo →</a>}
                </td>
                <td className="p-3 text-xs">{d.fecha_emision ? fmtDate(d.fecha_emision) : "—"}</td>
                <td className="p-3 text-xs">{d.fecha_vencimiento ? fmtDate(d.fecha_vencimiento) : "—"}</td>
                <td className="p-3 pr-5 text-right">
                  <button aria-label="Eliminar" onClick={() => del(d)} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Centro de documentos GENÉRICO: fotos, comprobantes adicionales, etc. */}
      <DocumentCenter
        entityType="client"
        entityId={cid}
        title="Archivos adicionales (fotos, comprobantes, anexos)"
      />
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...fm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border"><h3 className="font-heading text-lg font-semibold">Nuevo documento</h3></div>
            <form onSubmit={fm.guardar} className="p-5 space-y-3">
              <div><L label="Tipo *" />
                <select required value={f.tipo} data-testid="doc-tipo" onChange={(e) => setF({ ...f, tipo: e.target.value })}
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                  {Object.entries(TIPO_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><L label="Nombre *" /><input required data-testid="doc-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div><L label="URL (link a archivo / Drive / Dropbox)" /><input type="url" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><L label="Fecha emisión" /><input type="date" value={f.fecha_emision} onChange={(e) => setF({ ...f, fecha_emision: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
                <div><L label="Fecha vencimiento" /><input type="date" value={f.fecha_vencimiento} onChange={(e) => setF({ ...f, fecha_vencimiento: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              </div>
              <div><L label="Notas" /><textarea rows="2" value={f.notas} onChange={(e) => setF({ ...f, notas: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={fm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button disabled={fm.saving} type="submit" data-testid="doc-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function BanksTab({ cid, items, onChange }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ banco: "", numero_cuenta: "", tipo: "cheques", titular: "", moneda: "HNL" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/clients/${cid}/bank-accounts`, f); toast.success("Cuenta agregada"); setOpen(false); setF({ banco: "", numero_cuenta: "", tipo: "cheques", titular: "", moneda: "HNL" }); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const del = async (b) => {
    if (!window.confirm(`Eliminar ${b.banco} - ${b.numero_cuenta}?`)) return;
    try { await api.delete(`/clients/${cid}/bank-accounts/${b.id}`); toast.success("Eliminada"); onChange(); }
    catch (er) { toast.error(apiErr(er)); }
  };
  const fm = useModalForm({ value: f, abierto: open, onClose: () => setOpen(false), onSave: save });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setOpen(true)} data-testid="new-bank-btn"
          className="bg-agro-green text-black px-3 py-2 text-sm rounded-md font-medium flex items-center gap-2">
          <PlusIcon size={14} weight="bold" /> Agregar cuenta
        </button>
      </div>
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Banco</th>
              <th className="p-3">Cuenta</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Titular</th>
              <th className="p-3">Moneda</th>
              <th className="p-3 pr-5 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin cuentas bancarias</td></tr>
            ) : items.map((b) => (
              <tr key={b.id} className="border-t border-border/40" data-testid={`bank-row-${b.id}`}>
                <td className="p-3 pl-5 text-sm">{b.banco}</td>
                <td className="p-3 font-mono text-xs">{b.numero_cuenta}</td>
                <td className="p-3 text-xs capitalize">{b.tipo}</td>
                <td className="p-3 text-xs">{b.titular}</td>
                <td className="p-3 text-xs">{b.moneda}</td>
                <td className="p-3 pr-5 text-right">
                  <button aria-label="Eliminar" onClick={() => del(b)} className="p-1.5 text-text-muted hover:text-red-400"><TrashIcon size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...fm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-border"><h3 className="font-heading text-lg font-semibold">Nueva cuenta bancaria</h3></div>
            <form onSubmit={fm.guardar} className="p-5 space-y-3">
              <div><L label="Banco *" /><input required data-testid="bank-banco" value={f.banco} onChange={(e) => setF({ ...f, banco: e.target.value })} placeholder="BAC, Atlántida, Ficohsa…" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div><L label="Número de cuenta *" /><input required data-testid="bank-numero" value={f.numero_cuenta} onChange={(e) => setF({ ...f, numero_cuenta: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green font-mono" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><L label="Tipo" />
                  <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="cheques">Cheques</option><option value="ahorro">Ahorro</option><option value="tarjeta_credito">Tarjeta crédito</option><option value="otro">Otro</option>
                  </select>
                </div>
                <div><L label="Moneda" />
                  <select value={f.moneda} onChange={(e) => setF({ ...f, moneda: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="HNL">HNL — Lempiras</option><option value="USD">USD — Dólares</option><option value="EUR">EUR — Euros</option>
                  </select>
                </div>
              </div>
              <div><L label="Titular" /><input value={f.titular} onChange={(e) => setF({ ...f, titular: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" /></div>
              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={fm.cerrar} className="px-4 py-2 text-sm border border-border rounded-md">Cancelar</button>
                <button disabled={fm.saving} type="submit" data-testid="bank-submit" className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function HistoryTab({ statement }) {
  const movs = statement?.movimientos || [];
  const projectSummary = statement?.project_summary || [];
  const saldoFavor = statement?.saldo_a_favor || 0;
  const credits = statement?.credits || [];
  const invoices = statement?.invoices || [];
  const clienteId = statement?.client?.id;
  // PAG-04 — filtro por rango de fechas
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  // Modal aplicar saldo a favor
  const [applyOpen, setApplyOpen] = useState(false);
  const [recibo, setRecibo] = useState(null);   // detalle del recibo desde un movimiento de pago
  const filtered = movs.filter((m) => {
    if (!m.fecha) return true;
    const d = (m.fecha || "").slice(0, 10);
    if (desde && d < desde) return false;
    if (hasta && d > hasta) return false;
    return true;
  });
  const totalDebe = filtered.reduce((s, m) => s + (m.debe || 0), 0);
  const totalHaber = filtered.reduce((s, m) => s + (m.haber || 0), 0);
  return (
    <div className="space-y-4">
      {/* CLI-01 — Resumen por proyecto */}
      {projectSummary.length > 0 && (
        <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border/60">
            <div className="font-heading font-semibold">Resumen por proyecto</div>
            <div className="text-xs text-text-muted">Saldo dinámico = Facturado − Pagado − NC, por proyecto.</div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Código</th>
                <th className="p-3">Proyecto</th>
                <th className="p-3 text-right">Facturado</th>
                <th className="p-3 text-right">Pagado</th>
                <th className="p-3 text-right">NC</th>
                <th className="p-3 pr-5 text-right">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {projectSummary.map((p) => (
                <tr key={p.id} className="border-t border-border/40" data-testid={`proj-summary-${p.codigo}`}>
                  <td className="p-3 pl-5 text-xs font-mono text-agro-green">{p.codigo}</td>
                  <td className="p-3 text-sm">{p.nombre}</td>
                  <td className="p-3 text-right text-xs">{fmtL(p.facturado)}</td>
                  <td className="p-3 text-right text-xs text-agro-green">{fmtL(p.pagado)}</td>
                  <td className="p-3 text-right text-xs text-text-muted">{fmtL(p.notas_credito)}</td>
                  <td className="p-3 pr-5 text-right font-semibold">{fmtL(p.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {saldoFavor > 0.01 && (
        <div className="bg-agro-green/5 border border-agro-green/30 rounded-md p-3 text-sm flex justify-between items-center">
          <div>
            <div className="text-text-secondary">Saldo a favor disponible (anticipos)</div>
            <div className="text-[11px] text-text-muted">{credits.filter((c) => c.estado === "disponible").length} crédito(s) disponible(s)</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-heading font-semibold text-agro-green text-lg" data-testid="saldo-favor">{fmtL(saldoFavor)}</span>
            <button onClick={() => setApplyOpen(true)} data-testid="apply-credit-btn"
              className="bg-agro-green hover:bg-agro-green-hover text-black text-xs font-semibold rounded px-3 py-2">
              Aplicar a factura
            </button>
          </div>
        </div>
      )}

      {applyOpen && (
        <ApplyCreditModal
          clienteId={clienteId}
          saldoFavor={saldoFavor}
          credits={credits.filter((c) => c.estado === "disponible")}
          invoices={invoices.filter((i) => (i.saldo_pendiente || 0) > 0.01 && i.estado !== "anulada")}
          onClose={() => setApplyOpen(false)}
          onSaved={() => { setApplyOpen(false); window.location.reload(); }}
        />
      )}

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="font-heading font-semibold">Historial completo</div>
            <div className="text-xs text-text-muted">Cada proyecto figura individualmente. Cobros multi se desglosan por allocation.</div>
          </div>
          {/* PAG-04 — filtros de fecha */}
          <div className="flex flex-wrap items-end gap-2" data-testid="history-date-filter">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary block mb-1">Desde</label>
              <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
                data-testid="history-desde"
                className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary block mb-1">Hasta</label>
              <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
                data-testid="history-hasta"
                className="bg-app border border-border rounded-md px-2 py-1.5 text-xs" />
            </div>
            {(desde || hasta) && (
              <button onClick={() => { setDesde(""); setHasta(""); }}
                data-testid="history-clear"
                className="text-xs text-text-muted hover:text-text-primary px-2 py-1.5 border border-border rounded-md">
                Limpiar
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-5">Fecha</th>
              <th className="p-3">Tipo</th>
              <th className="p-3">Referencia</th>
              <th className="p-3">Proyecto</th>
              <th className="p-3">Concepto</th>
              <th className="p-3">Usuario</th>
              <th className="p-3 text-right">Debe</th>
              <th className="p-3 pr-5 text-right">Haber</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan="8" className="p-8 text-center text-text-muted">{movs.length === 0 ? "Sin historial" : "Sin movimientos en el rango seleccionado"}</td></tr>
            ) : filtered.map((m, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="p-3 pl-5 text-xs">{fmtDate(m.fecha)}</td>
                <td className="p-3 text-xs"><span className={`px-2 py-0.5 rounded text-[10px] ${
                  m.tipo === "Factura" ? "bg-app border border-border" :
                  m.tipo === "Pago" ? "bg-agro-green/10 text-agro-green border border-agro-green/30" :
                  m.tipo === "Anticipo" ? "bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30" :
                  "bg-blue-400/10 text-blue-300 border border-blue-400/30"
                }`}>{m.tipo}</span></td>
                <td className="p-3 text-xs font-mono">
                  {m.pago_id ? (
                    <button onClick={() => setRecibo(m.pago_id)} className="text-agro-green hover:underline" title="Ver recibo y su aplicación a facturas"
                      data-testid={`mov-recibo-${m.referencia}`}>{m.referencia}</button>
                  ) : m.referencia}
                </td>
                <td className="p-3 text-xs font-mono text-text-muted">{m.proyecto_codigo || "—"}</td>
                <td className="p-3 text-xs text-text-secondary">{m.concepto}</td>
                <td className="p-3 text-xs text-text-secondary">{m.usuario || ""}</td>
                <td className="p-3 text-right">{m.debe ? fmtL(m.debe) : "—"}</td>
                <td className="p-3 pr-5 text-right text-agro-green">{m.haber ? fmtL(m.haber) : "—"}</td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-sidebar">
              <tr className="text-xs">
                <td className="p-3 pl-5 font-semibold" colSpan="6">Subtotales del rango ({filtered.length} mov.)</td>
                <td className="p-3 text-right font-semibold">{fmtL(totalDebe)}</td>
                <td className="p-3 pr-5 text-right font-semibold text-agro-green">{fmtL(totalHaber)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {recibo && <ReciboDetalleModal pagoId={recibo} onClose={() => setRecibo(null)} />}
    </div>
  );
}


function ApplyCreditModal({ clienteId, saldoFavor, credits, invoices, onClose, onSaved }) {
  const [facturaId, setFacturaId] = useState("");
  const [monto, setMonto] = useState(0);
  const [saving, setSaving] = useState(false);
  const selectedInv = invoices.find((i) => i.id === facturaId);
  const maxAplicable = Math.min(saldoFavor, selectedInv?.saldo_pendiente || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!facturaId) { toast.error("Selecciona una factura"); return; }
    if (Number(monto) <= 0) { toast.error("Monto inválido"); return; }
    if (Number(monto) > maxAplicable + 0.01) { toast.error(`Excede saldo disponible. Máx: L. ${maxAplicable.toFixed(2)}`); return; }
    setSaving(true);
    try {
      await api.post(`/clients/${clienteId}/credits/apply`, {
        factura_id: facturaId,
        monto: Number(monto),
      });
      toast.success("Saldo a favor aplicado correctamente");
      onSaved();
    } catch (er) { toast.error(apiErr(er)); }
    finally { setSaving(false); }
  };

  const acm = useModalForm({ value: { facturaId, monto }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...acm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="apply-credit-modal">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-green">Aplicar saldo a favor</div>
            <h3 className="font-heading text-lg font-semibold">Anticipos disponibles: {fmtL(saldoFavor)}</h3>
          </div>
          <button aria-label="Cerrar" onClick={acm.cerrar} className="text-text-muted"><XIcon size={20} /></button>
        </div>
        <form onSubmit={acm.guardar} className="p-5 space-y-4">
          <div className="bg-app border border-border/60 rounded-md p-3 text-xs space-y-1">
            <div className="text-text-muted">Se consumirán {credits.length} crédito(s) FIFO (más antiguo primero):</div>
            {credits.slice(0, 5).map((c) => (
              <div key={c.id} className="flex justify-between text-text-secondary">
                <span>{c.concepto || "Anticipo"} · {(c.fecha || "").slice(0, 10)}</span>
                <span className="text-agro-green font-mono">{fmtL(c.monto_disponible)}</span>
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Factura destino *</label>
            <select value={facturaId} onChange={(e) => { setFacturaId(e.target.value); const f = invoices.find((x) => x.id === e.target.value); if (f) setMonto(Math.min(saldoFavor, f.saldo_pendiente)); }}
              data-testid="apply-factura"
              className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— Selecciona factura con saldo —</option>
              {invoices.map((i) => (
                <option key={i.id} value={i.id}>{i.numero} {i.proyecto_codigo && `· ${i.proyecto_codigo}`} · Saldo {fmtL(i.saldo_pendiente)}</option>
              ))}
            </select>
            {invoices.length === 0 && <div className="text-[11px] text-text-muted mt-1">No hay facturas con saldo pendiente.</div>}
          </div>
          {selectedInv && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">
                Monto a aplicar (máx L. {maxAplicable.toFixed(2)})
              </label>
              <input type="number" step="0.01" min="0" max={maxAplicable} value={monto}
                onChange={(e) => setMonto(e.target.value)} data-testid="apply-monto"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-base focus:border-agro-green" />
            </div>
          )}
          <button type="submit" disabled={(saving || !facturaId || Number(monto) <= 0) || acm.saving}
            data-testid="apply-credit-submit"
            className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-2.5 disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? "Aplicando…" : `Aplicar ${fmtL(Number(monto) || 0)}`}
          </button>
        </form>
      </div>
    </div>
  );
}
